/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ParentalControlServiceImpl } from '../impls/ParentalControlServiceImpl';
import { IParentalControlService } from '../interfaces';
import {
    TInternalRating,
    TMetadata,
    TPCRatingsData,
    TPCServiceParams,
    TServicesParams,
} from '../types';

export class ParentalControlService implements IParentalControlService {
    private oParentalControlServiceImpl: IParentalControlService;

    public constructor(params: TServicesParams & TPCServiceParams) {
        this.oParentalControlServiceImpl = new ParentalControlServiceImpl(params);
    }

    /**
     * @inheritdoc
     */
    public checkPCViolations(metadata: TMetadata): boolean {
        return this.oParentalControlServiceImpl.checkPCViolations(metadata);
    }

    /**
     * @inheritdoc
     */
    public checkPCViolationsWithRatings(
        internalRatings: TInternalRating[],
        isAdult: boolean,
        contentType: string,
    ): boolean {
        return this.oParentalControlServiceImpl.checkPCViolationsWithRatings(
            internalRatings,
            isAdult,
            contentType,
        );
    }

    /**
     * @inheritdoc
     */
    public getPCSetting(key: string): string {
        return this.oParentalControlServiceImpl.getPCSetting(key);
    }

    /**
     * @inheritdoc
     */
    public isPCHideAdultContentEnabled(): boolean {
        return this.oParentalControlServiceImpl.isPCHideAdultContentEnabled();
    }

    /**
     * @inheritdoc
     */
    public loadPCSettings(): void {
        this.oParentalControlServiceImpl.loadPCSettings();
    }

    /**
     * @inheritdoc
     */
    public updatePCSetting(key: string, value: string): void {
        return this.oParentalControlServiceImpl.updatePCSetting(key, value);
    }

    /**
     * @inheritdoc
     */
    public verifyPCPin(pin: string): boolean {
        return this.oParentalControlServiceImpl.verifyPCPin(pin);
    }

    /**
     * @inheritdoc
     */
    public createOptionsList(): void {
        return this.oParentalControlServiceImpl.createOptionsList();
    }

    /**
     * @inheritdoc
     */
    public getPCHighestAllowedRatings(): Array<{ [key: string]: string }> {
        return this.oParentalControlServiceImpl.getPCHighestAllowedRatings();
    }

    /**
     * @inheritdoc
     */
    public getPCUnratedContentAllowed(): Array<{ [key: string]: string }> {
        return this.oParentalControlServiceImpl.getPCUnratedContentAllowed();
    }

    /**
     * @inheritdoc
     */
    public getPCRatingLevel(ratingTypeId: string, ratingValueId: string): number | undefined {
        return this.oParentalControlServiceImpl.getPCRatingLevel(ratingTypeId, ratingValueId);
    }

    /**
     * @inheritdoc
     */
    public getMatchedRating(internalRatings: TInternalRating[]): TInternalRating[] {
        return this.oParentalControlServiceImpl.getMatchedRating(internalRatings);
    }

    /**
     * @inheritdoc
     */
    public checkRatingViolations(internalRatings: Required<TInternalRating>): boolean {
        return this.oParentalControlServiceImpl.checkRatingViolations(internalRatings);
    }

    /**
     * @inheritdoc
     */
    public getPCMovieRatingTypeId(): string {
        return this.oParentalControlServiceImpl.getPCMovieRatingTypeId();
    }

    /**
     * @inheritdoc
     */
    public getPCTVRatingTypeId(): string {
        return this.oParentalControlServiceImpl.getPCTVRatingTypeId();
    }

    /**
     * @inheritdoc
     */
    public getPCRatingsData(): TPCRatingsData[] {
        return this.oParentalControlServiceImpl.getPCRatingsData();
    }

    /**
     * @inheritdoc
     */
    public getPCOptionList(): Array<{
        [key: string]: {
            [key: string]: { level: number; description: string; ratingValueName: string };
        }[];
    }> {
        return this.oParentalControlServiceImpl.getPCOptionList();
    }

    /**
     * @inheritdoc
     */
    public getPCRatingOptions(): Array<{
        [key: string]: {
            [key: string]: { level: number; description: string; ratingValueName: string };
        }[];
    }> {
        return this.oParentalControlServiceImpl.getPCRatingOptions();
    }
}
